import React, { useState, useMemo } from 'react';
import Select from 'react-select';
import { useParams, useNavigate, Link } from 'react-router-dom';
import ReportIssueIntake from '../components/ReportIssueIntake';
import { ImpactBadge, TierBadge, AssigneePill, DEFAULT_TRIAGE_TIER, isNeedsTriage, relativeTime } from '../components/TicketUi';
import { MaintenanceHistoryList } from '../components/MaintenanceUi';
import { DSButton, DSCard, DSCheckbox, DSInput, DSSelect, DSTextarea, DSIcon } from '../design-system/primitives';
import { formatScoreAbbreviated } from '../utils/formatScore';
import useMachineDetail from '../hooks/useMachineDetail';
import { scorekeepingUrl } from './scorekeeping/scorekeepingApi';
import '../components/TicketUi.css';
import '../components/MaintenanceUi.css';
import './MachineDetail.css';

function MachineDetail() {
  const { machineId } = useParams();
  const navigate = useNavigate();
  const [photoModal, setPhotoModal] = useState(null);
  const {
    machine,
    loading,
    error,
    setError,
    editingNotes,
    setEditingNotes,
    setupNotes,
    setSetupNotes,
    activeTab,
    setActiveTab,
    scores,
    months,
    selectedPeriod,
    setSelectedPeriod,
    boardPeriod,
    loadingScores,
    showEditModal,
    editForm,
    users,
    pintips,
    loadingPintips,
    pintipsError,
    showReportIssueModal,
    setShowReportIssueModal,
    issueReporterName,
    setIssueReporterName,
    submittingIssue,
    issueSubmitSuccess,
    userUpvotes,
    knownIssues,
    showKnownIssueModal,
    setShowKnownIssueModal,
    knownIssueForm,
    setKnownIssueForm,
    savingKnownIssue,
    knownIssueError,
    isAdmin,
    canManageMachineIssues,
    canManageKnownIssues,
    handleSaveNotes,
    handleGenerateQR,
    handleOpenEditModal,
    handleCloseEditModal,
    handleEditFormChange,
    handleSaveEdit,
    handleSubmitIssue,
    handleReportDone,
    handleUpvote,
    openKnownIssueModal,
    submitKnownIssue,
    handleRetireKnownIssue,
    maintenanceHistory,
    loadingMaintenanceHistory
  } = useMachineDetail(machineId);

  const machineOwnerOptions = useMemo(() => {
    const baseOptions = (users || []).map(user => ({
      value: user.id,
      label: `${user.first_name || ''} ${user.last_name || ''} (${user.username})`.trim(),
      searchText: `${user.first_name || ''} ${user.last_name || ''} ${user.username || ''} ${user.email || ''}`.toLowerCase()
    }));

    return [{ value: '__club__', label: 'Club Machine (No Owner)', searchText: 'club machine no owner' }, ...baseOptions];
  }, [users]);

  const selectedMachineOwnerOption = useMemo(() => {
    if (editForm.ownerUserId === null || editForm.ownerUserId === undefined) {
      return machineOwnerOptions.find(option => option.value === '__club__') || null;
    }

    return machineOwnerOptions.find(option => String(option.value) === String(editForm.ownerUserId)) || null;
  }, [editForm.ownerUserId, machineOwnerOptions]);

  const handleIssueCardClick = (issueId) => {
    if (!canManageMachineIssues) {
      return;
    }

    navigate(`/machine-issues/${issueId}`);
  };

  if (loading) return <div className="loading">Loading machine details...</div>;
  if (!machine) return <div className="error">Machine not found</div>;

  const openIssues = machine.issues?.filter((issue) => String(issue.status || '').toLowerCase() === 'open') || [];

  return (
    <div className="machine-detail">
      <div className="md-back-row">
        <button type="button" className="md-back-btn" onClick={() => navigate('/machines')}>
          <DSIcon name="arrowLeft" /> Machines
        </button>
      </div>

      {error && <div className="error-message">{error}</div>}

      {/* Machine Header with Image */}
      {machine.image_url_large && (
        <div className="machine-header-image">
          <img 
            src={machine.image_url_large} 
            alt={machine.name}
            className="machine-hero-image"
          />
        </div>
      )}

      {/* Tab Navigation */}
      <div className="tab-navigation">
        <button 
          className={`tab ${activeTab === 'info' ? 'active' : ''}`}
          onClick={() => setActiveTab('info')}
        >
          Info
        </button>
        <button 
          className={`tab ${activeTab === 'pintips' ? 'active' : ''}`}
          onClick={() => setActiveTab('pintips')}
        >
          Pintips
        </button>
        <button 
          className={`tab ${activeTab === 'issues' ? 'active' : ''}`}
          onClick={() => setActiveTab('issues')}
        >
          Issues
        </button>
        <button
          className={`tab ${activeTab === 'maintenance' ? 'active' : ''}`}
          onClick={() => setActiveTab('maintenance')}
        >
          Maintenance
        </button>
        <button 
          className={`tab ${activeTab === 'scores' ? 'active' : ''}`}
          onClick={() => setActiveTab('scores')}
        >
          Scores
        </button>
      </div>

      <div className="tab-content">
        {/* Info Tab */}
        {activeTab === 'info' && (
          <div className="info-section">
          <h2>Machine Information</h2>
          <div className="info-grid">
            <div className="info-item">
              <label>Manufacturer:</label>
              <span>{machine.manufacturer || 'N/A'}</span>
            </div>
            <div className="info-item">
              <label>Year:</label>
              <span>{machine.year || 'N/A'}</span>
            </div>
            <div className="info-item">
              <label>Owner:</label>
              <span>{machine.owner_name || 'Club Machine'}</span>
            </div>
            <div className="info-item">
              <label>Pingolf Target:</label>
              <span>{machine.pingolf_target_score ? formatScoreAbbreviated(machine.pingolf_target_score) : 'Not Set'}</span>
            </div>
            <div className="info-item">
              <label>Number of Balls:</label>
              <span>{machine.pingolf_ball_count || 'Not Set'}</span>
            </div>
            {isAdmin && (
            <>
            <div className="info-item">
              <label>OPDB ID:</label>
              <span>{machine.opdb_id || 'N/A'}</span>
            </div>
            <div className="info-item">
              <label>IPDB ID:</label>
              <span>{machine.ipdb_id || 'N/A'}</span>
            </div>
            <div className="info-item">
              <label>MatchPlay Arena ID:</label>
              <span>{machine.matchplay_arena_id || 'N/A'}</span>
            </div>
            </>
            )}
            {machine.is_repin && (
            <div className="info-item">
              <label>Re-Pin:</label>
              <span>This is the Re-Pin machine ($1 coin drop)</span>
            </div>
            )}
          </div>

          {isAdmin && (
          <div className="actions">
            <DSButton onClick={handleOpenEditModal} appearance="outline" tone="secondary">
              Edit Machine
            </DSButton>
            <DSButton onClick={handleGenerateQR} appearance="outline" tone="secondary">
              Download QR Code
            </DSButton>
          </div>
          )}

          <div className="notes-section info-notes-section">
            <div className="section-header">
              <h2>Setup Notes & Tournament Settings</h2>
              {!editingNotes && isAdmin && (
                <DSButton onClick={() => setEditingNotes(true)} appearance="outline" tone="secondary" size="sm">
                  Edit Notes
                </DSButton>
              )}
            </div>

            {editingNotes ? (
              <div className="notes-editor">
                <DSTextarea
                  value={setupNotes}
                  onChange={(e) => setSetupNotes(e.target.value)}
                  rows="10"
                  placeholder="Enter tournament settings, known quirks, pintips, etc."
                />
                <div className="editor-actions">
                  <DSButton onClick={handleSaveNotes} appearance="solid" tone="primary">
                    Save Notes
                  </DSButton>
                  <DSButton
                    onClick={() => {
                      setEditingNotes(false);
                      setSetupNotes(machine.setup_notes || '');
                    }}
                    appearance="outline"
                    tone="neutral"
                  >
                    Cancel
                  </DSButton>
                </div>
              </div>
            ) : (
              <div className="notes-display">
                {machine.setup_notes ? (
                  <pre>{machine.setup_notes}</pre>
                ) : (
                  <p className="no-notes">
                    {isAdmin
                      ? 'No setup notes available. Click "Edit Notes" to add tournament settings and pintips.'
                      : 'No setup notes available for this machine.'}
                  </p>
                )}
              </div>
            )}
          </div>
        </div>
        )}

        {/* Pintips Tab */}
        {activeTab === 'pintips' && (
          <div className="pintips-section">
            {loadingPintips ? (
              <div className="loading">Loading pintips...</div>
            ) : pintipsError ? (
              <div className="no-content">
                <p>{pintipsError}</p>
              </div>
            ) : pintips.length === 0 ? (
              <div className="no-content">
                <p>No pintips available for this machine yet.</p>
              </div>
            ) : (
              <div className="pintips-list">
                {pintips.map((tip) => (
                  <DSCard key={tip.tipId} cornerVariant="md" className="pintip-card">
                    <div className="pintip-header">
                      <span className="pintip-category">{tip.category}</span>
                      <span className="pintip-votes"><DSIcon name="thumbsUp" /> {tip.voteTotal}</span>
                    </div>
                    <div className="pintip-text">{tip.text}</div>
                  </DSCard>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Issues Tab */}
        {activeTab === 'issues' && (
          <div className="issues-tab ticket-ui">
            <div className="issues-section">
              <h2>Open Issues ({openIssues.length})</h2>
              {openIssues.length > 0 ? (
                <div className="issues-list">
                  {openIssues.map(issue => (
                    <DSCard
                      key={issue.id}
                      cornerVariant="md"
                      className={`issue-card ${canManageMachineIssues ? 'clickable' : ''}`}
                      onClick={canManageMachineIssues ? () => handleIssueCardClick(issue.id) : undefined}
                    >
                      <div className="issue-content">
                        <p className="issue-description">{issue.description}</p>
                        <div className="issue-badges">
                          <ImpactBadge impact={issue.impact} />
                          {isNeedsTriage(issue) ? (
                            <TierBadge tier={DEFAULT_TRIAGE_TIER} assumed />
                          ) : (
                            <TierBadge tier={issue.required_tech_tier} />
                          )}
                          <AssigneePill name={issue.assigned_to_name} />
                        </div>
                        <div className="issue-meta">
                          {issue.reporter_name} • {relativeTime(issue.created_at)}
                          {issue.maintenance_record_id && <span className="issue-meta__maintenance-tag"> · Found during maintenance</span>}
                        </div>
                      </div>
                      <button
                        className={`upvote-btn ${userUpvotes.has(issue.id) ? 'upvoted' : ''}`}
                        onClick={(event) => {
                          event.stopPropagation();
                          handleUpvote(issue.id);
                        }}
                      >
                        <DSIcon name="thumbsUp" /> {issue.upvote_count || 0}
                      </button>
                    </DSCard>
                  ))}
                </div>
              ) : (
                <p className="no-issues">No open issues.</p>
              )}

              <div className="add-issue-section">
                <DSButton
                  appearance="solid"
                  tone="primary"
                  onClick={() => setShowReportIssueModal(true)}
                >
                  Report an Issue
                </DSButton>
              </div>
            </div>

            <div className="known-issues-section">
              <div className="section-header">
                <h2>Known Issues ({knownIssues.length})</h2>
                {canManageKnownIssues && (
                  <DSButton appearance="outline" tone="secondary" size="sm" onClick={openKnownIssueModal}>
                    Add Known Issue
                  </DSButton>
                )}
              </div>
              {knownIssues.length > 0 ? (
                <div className="issues-list">
                  {knownIssues.map((ki) => (
                    <DSCard key={ki.id} cornerVariant="md" className="known-issue-card">
                      <div className="known-issue-card__header">
                        <p className="known-issue-card__description">{ki.description}</p>
                        {canManageKnownIssues && (
                          <DSButton
                            appearance="outline"
                            tone="secondary"
                            size="sm"
                            className="known-issue-card__retire"
                            onClick={() => handleRetireKnownIssue(ki.id)}
                          >
                            Retire
                          </DSButton>
                        )}
                      </div>
                      {ki.guidance_text && <div className="known-issue-card__guidance">{ki.guidance_text}</div>}
                      {ki.source_issue_id && (
                        <div className="known-issue-card__source">
                          <Link to={`/machine-issues/${ki.source_issue_id}`}>From ticket #{ki.source_issue_id}</Link>
                        </div>
                      )}
                    </DSCard>
                  ))}
                </div>
              ) : (
                <p className="no-issues">No known issues logged for this machine.</p>
              )}
            </div>
          </div>
        )}

        {/* Maintenance Tab */}
        {activeTab === 'maintenance' && (
          <div className="maintenance-tab-section maintenance-ui">
            <div className="section-header">
              <h2>Maintenance Log</h2>
              <Link
                to={`/machines/maintenance/${machineId}`}
                state={{ returnTo: `/machines/${machineId}`, returnLabel: 'Back to Machine' }}
                className="md-maintenance-checklist-link"
              >
                Open current checklist <DSIcon name="arrowRight" />
              </Link>
            </div>
            {loadingMaintenanceHistory ? (
              <div className="loading">Loading maintenance history...</div>
            ) : (
              <MaintenanceHistoryList
                records={maintenanceHistory}
                emptyMessage="No maintenance history logged for this machine yet."
              />
            )}
          </div>
        )}

        {/* Scores Tab */}
        {activeTab === 'scores' && (
          <div className="scores-section">
            {/* Month picker and the way onto the board share one row. They were
                stacked with the sync time between them, which spent three rows
                of a phone screen before a single score was visible. */}
            <div className="scores-header">
              <div className="scores-controls">
                {/* Competition months from scorekeeping, opening on the month
                    in progress. There is no All Time option: the board is a
                    month's competition, and scorekeeping has no all-time view
                    of a single machine to show here. */}
                <DSSelect
                  value={selectedPeriod || boardPeriod || ''}
                  onChange={(e) => setSelectedPeriod(e.target.value || null)}
                  aria-label="Competition month"
                >
                  {months.length === 0 && boardPeriod && (
                    <option value={boardPeriod}>{boardPeriod}</option>
                  )}
                  {months.map(month => (
                    <option key={month.period} value={month.period}>
                      {month.label}
                    </option>
                  ))}
                </DSSelect>

                {/* Scores are entered in the club's own app now, so the board
                    and the way to add to it belong on the same screen. Before
                    this the only route in was the QR code on the machine
                    itself, which is no help to somebody already looking at the
                    board. */}
                <DSButton
                  to={`/scorekeeping/machines/${machineId}/submit`}
                  appearance="solid"
                  tone="primary"
                  leadingIcon={<DSIcon name="plus" size="sm" />}
                >
                  Submit a score
                </DSButton>
              </div>
            </div>
            
            {loadingScores ? (
              <div className="loading">Loading scores...</div>
            ) : scores.length === 0 ? (
              <div className="no-content">
                <p>No scores recorded yet.</p>
                <p>Be the first. Submit a score above, or scan the QR on the machine.</p>
              </div>
            ) : (
              <div className="scores-table-container">
                <table className="scores-table">
                  <thead>
                    <tr>
                      <th>Rank</th>
                      <th>Player</th>
                      <th>Score</th>
                    </tr>
                  </thead>
                  <tbody>
                    {scores.map((entry) => (
                      <tr
                        key={entry.scoreId ?? `${entry.rank}-${entry.playerName}`}
                        onClick={() => setPhotoModal(entry)}
                        className="score-row-clickable"
                      >
                        {/* The board's own rank, so tied scores share a place
                            and a medal rather than being split by row order. */}
                        <td className="rank-cell">
                          {entry.rank <= 3 ? <DSIcon name="medal" /> : entry.rank}
                        </td>
                        <td>
                          <div className="player-name">{entry.playerName}</div>
                        </td>
                        <td className="score-cell">
                          {Number(entry.score).toLocaleString()}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {/* Photo Modal */}
        {photoModal && (
          <div className="modal-overlay" onClick={() => setPhotoModal(null)}>
            <div className="modal-content" onClick={(e) => e.stopPropagation()}>
              <button className="modal-close" onClick={() => setPhotoModal(null)}>
                <DSIcon name="x" />
              </button>
              <div className="modal-body">
                <h3>{photoModal.playerName}</h3>
                <div className="modal-score">
                  Score: {Number(photoModal.score).toLocaleString()}
                </div>
                {/* A score the machine reported itself has no photo at all. */}
                {(() => {
                  const photo = photoModal.photoPath
                    ? scorekeepingUrl(`/photos/${photoModal.photoPath}`)
                    : null;
                  if (!photo) {
                    return (
                      <p className="modal-no-photo">
                        {photoModal.source === 'stern'
                          ? 'Recorded by the machine, so there is no photo to show.'
                          : 'No photo on this one.'}
                      </p>
                    );
                  }
                  return (
                    <div className="modal-image-container">
                      <img
                        src={photo}
                        alt="Score"
                        className="modal-image"
                        onError={(e) => {
                          e.target.src = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="400" height="300"%3E%3Crect fill="%23ddd" width="400" height="300"/%3E%3Ctext x="50%25" y="50%25" dominant-baseline="middle" text-anchor="middle" fill="%23999"%3EPhoto not available%3C/text%3E%3C/svg%3E';
                        }}
                      />
                    </div>
                  );
                })()}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Edit Machine Modal */}
      {showEditModal && (
        <div className="modal-overlay machine-edit-modal" onClick={handleCloseEditModal}>
          <div className="modal-content" onClick={e => e.stopPropagation()}>
            <button className="modal-close" onClick={handleCloseEditModal}><DSIcon name="x" /></button>
            <h2>Edit Machine Settings</h2>
            <p className="modal-subtitle">
              Machine details like name, manufacturer, and year are synced from MatchPlay/OPDB.
            </p>
            
            <div className="edit-form">
              <div className="form-group">
                <label>Owner:</label>
                <Select
                  inputId="machine-owner"
                  className="machine-owner-picker"
                  classNamePrefix="machine-owner-select"
                  options={machineOwnerOptions}
                  value={selectedMachineOwnerOption}
                  onChange={(option) => {
                    if (!option || option.value === '__club__') {
                      handleEditFormChange('ownerUserId', null);
                      handleEditFormChange('ownerDisplay', 'Club Machine (No Owner)');
                      return;
                    }

                    handleEditFormChange('ownerUserId', option.value);
                    handleEditFormChange('ownerDisplay', option.label);
                  }}
                  isClearable
                  isSearchable
                  placeholder="Search by name, username, or email"
                  menuPortalTarget={document.body}
                  noOptionsMessage={() => 'No matching users'}
                  filterOption={(candidate, input) => {
                    if (!input) {
                      return true;
                    }
                    return candidate.data.searchText.includes(input.toLowerCase());
                  }}
                />
                <small className="help-text">Type to search members, or select "Club Machine" for club-owned machines</small>
              </div>
              
              <div className="form-group">
                {/* Status itself is not editable: it comes from the open
                    tickets. The only thing an admin decides is whether the
                    machine is still in the club. */}
                <DSSelect
                  label="Machine is:"
                  value={editForm.status === 'removed' ? 'removed' : 'active'}
                  onChange={(e) => handleEditFormChange('status', e.target.value)}
                >
                  <option value="active">In the club</option>
                  <option value="removed">Removed (retired)</option>
                </DSSelect>
                <p className="form-hint">
                  Operational, Needs Attention and Out of Order come from the open tickets: a Game Down
                  ticket makes it Out of Order, a Tournament Risk makes it Needs Attention, otherwise
                  Operational. Report or resolve a ticket to change it.
                </p>
              </div>

              <div className="form-group">
                <DSInput
                  label="WordPress Post ID:"
                  type="text"
                  value={editForm.wordpressPostId || ''}
                  onChange={(e) => handleEditFormChange('wordpressPostId', e.target.value)}
                  placeholder="e.g., 12345"
                />
                <small className="help-text">Link this machine to an existing WordPress page</small>
              </div>

              <div className="form-group">
                <DSCheckbox
                  label="Stern Insider Connected:"
                  checked={!!editForm.hasInsiderConnected}
                  onChange={(e) => handleEditFormChange('hasInsiderConnected', e.target.checked)}
                />
                <small className="help-text">Hide manual score submission when Insider Connected is enabled</small>
              </div>

              <div className="form-group">
                <DSCheckbox
                  id="machine-repin"
                  label="Re-Pin Machine:"
                  checked={!!editForm.isRepin}
                  onChange={(e) => handleEditFormChange('isRepin', e.target.checked)}
                />
                <small className="help-text">The one machine on $1 coin drop in the Re-Pin Program. Turning this on takes it off the current Re-Pin machine. The website's Re-Pin, Games and Visit pages follow it.</small>
              </div>
              
              <div className="form-group">
                <DSInput
                  label="Pingolf Target Score:"
                  type="number"
                  value={editForm.pingolfTargetScore || ''}
                  onChange={(e) => handleEditFormChange('pingolfTargetScore', e.target.value ? parseInt(e.target.value) : null)}
                  placeholder="e.g., 5000000"
                />
                <small className="help-text">Target score for pingolf competitions</small>
              </div>

              <div className="form-group">
                <DSInput
                  label="Number of Balls:"
                  type="number"
                  min="1"
                  value={editForm.pingolfBallCount || ''}
                  onChange={(e) => handleEditFormChange('pingolfBallCount', e.target.value ? parseInt(e.target.value) : null)}
                  placeholder="e.g., 3"
                />
                <small className="help-text">How many balls this machine plays per game (used as par when this machine is used for pingolf)</small>
              </div>

              <div className="form-group">
                <DSInput
                  label="Image URL:"
                  type="text"
                  value={editForm.imageUrl || ''}
                  onChange={(e) => handleEditFormChange('imageUrl', e.target.value)}
                  placeholder="https://..."
                />
                <small className="help-text">
                  {machine.image_is_manual
                    ? 'Manually set - MatchPlay/OPDB sync will not overwrite this. Clear the field to let OPDB manage it again.'
                    : 'Set a photo directly when OPDB has none for this machine. Leave blank to let OPDB/MatchPlay sync manage it automatically.'}
                </small>
              </div>
            </div>
            
            <div className="modal-actions equal-buttons">
              <DSButton onClick={handleSaveEdit} appearance="solid" tone="primary" className="machine-edit-modal__btn">Save Changes</DSButton>
              <DSButton onClick={handleCloseEditModal} appearance="outline" tone="neutral" className="machine-edit-modal__btn">Cancel</DSButton>
            </div>
          </div>
        </div>
      )}

      {/* Report Issue Modal */}
      {showReportIssueModal && (
        <div className="modal-overlay report-issue-modal" onClick={() => setShowReportIssueModal(false)}>
          <div className="modal-content" onClick={e => e.stopPropagation()}>
            <button className="modal-close" onClick={() => setShowReportIssueModal(false)}>
              <DSIcon name="x" />
            </button>
            {!issueSubmitSuccess && <h2>Report an Issue</h2>}

            <ReportIssueIntake
              reporterName={issueReporterName}
              onReporterNameChange={setIssueReporterName}
              reporterNameReadOnly
              machineName={machine.name}
              knownIssues={knownIssues}
              openIssues={openIssues.map((issue) => ({ ...issue, user_has_upvoted: userUpvotes.has(issue.id) }))}
              onUpvote={handleUpvote}
              submitting={submittingIssue}
              error={error}
              success={issueSubmitSuccess}
              onSubmit={handleSubmitIssue}
              onDone={handleReportDone}
            />
          </div>
        </div>
      )}

      {/* Add Known Issue Modal */}
      {showKnownIssueModal && (
        <div className="modal-overlay" onClick={() => setShowKnownIssueModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <button className="modal-close" onClick={() => setShowKnownIssueModal(false)}><DSIcon name="x" /></button>
            <h2>Add Known Issue</h2>
            <form onSubmit={submitKnownIssue} className="edit-form">
              <div className="form-group">
                <DSTextarea
                  label="Description:"
                  value={knownIssueForm.description}
                  onChange={(e) => setKnownIssueForm({ ...knownIssueForm, description: e.target.value })}
                  rows="3"
                  placeholder="e.g., Left orbit rubber comes loose periodically"
                  required
                  state={knownIssueError ? 'error' : undefined}
                />
              </div>
              <div className="form-group">
                <DSTextarea
                  label="Guidance (optional):"
                  value={knownIssueForm.guidanceText}
                  onChange={(e) => setKnownIssueForm({ ...knownIssueForm, guidanceText: e.target.value })}
                  rows="3"
                  placeholder="What should a reporter/tech do if they see this?"
                />
              </div>
              {knownIssueError && <div className="error-message">{knownIssueError}</div>}
              <div className="modal-actions">
                <DSButton type="submit" appearance="solid" tone="primary" disabled={savingKnownIssue} className="md-modal-btn">
                  {savingKnownIssue ? 'Saving...' : 'Add Known Issue'}
                </DSButton>
                <DSButton type="button" onClick={() => setShowKnownIssueModal(false)} appearance="outline" tone="neutral" className="md-modal-btn">
                  Cancel
                </DSButton>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export default MachineDetail;
